import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar
} from '@mui/material';
import { lazy, useEffect, useRef, useState } from 'react';
import { widgetPreviewSx as S, widgetPreviewSx } from './style';
import { widgetTexts as T } from './texts';
import StarIcon from '@mui/icons-material/Star';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import { KeywordTrackingAnalyzeCss } from 'src/content/keywordResearch/keywordTracking/KeywordTrackingAllCss';
import { applyRule, convertReviewRows, isWithinDateRange } from './functions';
import { KeywordOverviewResultCardsCss } from 'src/content/keywordResearch/keywordOverview/keywordOverviewCSS';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';
import HelpIcon from '@mui/icons-material/Help';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import Slider, { LazyLoadTypes } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import useIsMobile from 'src/common/CheckDevice';
import { getReviewWidget } from 'src/common/Scraper';
import { useParams } from 'react-router';
import { setPreviewCustomCss } from 'src/redux/localSeo';
import VerifiedIcon from '@mui/icons-material/Verified';
import { openNewTab } from 'src/common/config/FunctionHelper';
import { scriptURL } from 'src/appConfig';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';
import CommonModal from 'src/common/config/CommonModal';
import { ViewReviewModal } from '../ViewReviewModal';
import { parseRatingValue } from 'src/common/config/FunctionHelperPartTwo';

export const WidgetPreviewComponent = ({
  options,
  isScript = null,
  isFromList = null,
  optionsFromList = null,
  widgetId = null
}: any) => {
  const [dynamicOptions, setDynamicOptions] = useState({});
  const {
    template,
    cardBgColor,
    textColor,
    starsColor,
    accentColor,
    font,
    cardRadius,
    bgColor,
    enableExternalLinks,
    openLinksInNewTab,
    showProfilePictures,
    showNames,
    showCTAButton,
    buttonText,
    linkTextColor,
    buttonLabelColor,
    excludeRule,
    includeRule,
    customCSS,
    ratingFilter,
    dateFilter,
    widgetName,
    customCssToggle,
    showOnlyCommentedReviews
  } = isFromList
    ? optionsFromList || {}
    : isScript
    ? dynamicOptions || {}
    : options || {};

  const [view, setView] = useState<'desktop' | 'mobile'>('desktop');
  const [dynamicCustomCSS, setDynamicCustomCSS] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState<{
    [key: number]: boolean;
  }>({});
  const { id } = useParams<{ id: string }>();
  const isMobileUI = useIsMobile(600);
  const dispatch = useDispatch();
  const reviewList =
    useSelector((state: RootState) => state.localSeo.reviewList) ?? null;
  const previewCustomCss =
    useSelector((state: RootState) => state.localSeo.previewCustomCss) ?? null;
  const [rows, setRows] = useState(reviewList?.reviewResults || []);
  const [placeId, setPlaceId] = useState(reviewList?.placeId || '');
  const [totalReviewsRating, setTotalReviewsRating] = useState(
    reviewList?.totalReviewsRating || ''
  );
  const reviewUrl = `http://search.google.com/local/writereview?placeid=${placeId}`;
  const sliderRef = useRef<Slider | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const reviewTemplateRef = useRef<HTMLDivElement | null>(null);
  const [reviewCardWidth, setReviewCardWidth]: any = useState<number | 'auto'>(
    'auto'
  );
  const isMobile =
    view === 'mobile' || isMobileUI || (reviewCardWidth < 600 && !isFromList);
  const [slidesToShow, setSlidesToShow] = useState(isMobile ? 1 : 3);
  const [boxWidth, setBoxWidth] = useState<number | 'auto'>('auto');
  const [openViewModal, setOpenViewModal]: any = useState(null);
  const [selectedRow, setSelectedRow]: any = useState(null);
  const [locationName, setLocationName] = useState(
    reviewList?.locationName || ''
  );

  //  State for the global absolute index
  const [absoluteIndex, setAbsoluteIndex] = useState(0);
  // Create a "lock" to prevent overlapping animations
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const CARD_GAP = 16; // px gap between cards

  //  Helper to convert star string to numeric
  const getStarValue = (star: string) => {
    switch (star) {
      case 'FIVE':
        return 5;
      case 'FOUR':
        return 4;
      case 'THREE':
        return 3;
      case 'TWO':
        return 2;
      case 'ONE':
        return 1;
      default:
        return 0;
    }
  };

  //  Pick first review with highest available stars
  const reviewOrder = ['FIVE', 'FOUR', 'THREE', 'TWO', 'ONE'];
  let selectedReview = null;

  for (let star of reviewOrder) {
    selectedReview = rows?.find((r: any) => r.starRating === star);
    if (selectedReview) break;
  }

  // Calculate dynamic best and worst rating from rows
  const starValues = rows?.map((r: any) => getStarValue(r.starRating)) || [];
  const bestRating = starValues?.length ? Math.max(...starValues) : 5;
  const worstRating = starValues?.length ? Math.min(...starValues) : 1;
  const siteUrl = window.location.href || '';
  const metaDescription =
    document
      .querySelector('meta[name="description"]')
      ?.getAttribute('content') || '';
  const siteLogo =
    document.querySelector('link[rel="icon"]')?.getAttribute('href') ||
    document.querySelector('img.logo')?.getAttribute('src') ||
    '';
  // --------------------------------------
  // SAFE HELPERS
  // --------------------------------------
  const safeString = (value, fallback = '') =>
    value && typeof value === 'string' && value.trim().length > 0
      ? value
      : fallback;

  const safeNumber = (value, fallback = 0) =>
    typeof value === 'number' && !isNaN(value) && value > 0 ? value : fallback;

  // --------------------------------------
  // REQUIRED FALLBACKS
  // --------------------------------------
  const finalName = safeString(locationName, 'Business');
  const finalUrl = safeString(siteUrl, window.location.origin);
  const finalImage = safeString(
    siteLogo,
    `${window.location.origin}/favicon.ico`
  );
  const finalDescription = safeString(
    metaDescription,
    `${finalName} customer reviews`
  );
  const finalReviewCount = safeNumber(rows?.length, 1);
  const finalRatingValue = safeNumber(parseRatingValue(totalReviewsRating), 5);

  // --------------------------------------
  // REVIEW SCHEMA (if exists)
  // --------------------------------------
  let reviewSchema = [];

  if (selectedReview) {
    reviewSchema.push({
      '@type': 'Review',
      reviewBody: safeString(selectedReview?.comment, 'Excellent service!'),
      datePublished: safeString(
        selectedReview?.createTime?.split('T')[0],
        '2025-01-01'
      ),
      author: {
        '@type': 'Person',
        name: safeString(
          selectedReview?.reviewer?.displayName,
          'Verified Customer'
        )
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: safeNumber(getStarValue(selectedReview?.starRating), 5),
        bestRating: 5,
        worstRating: 1
      }
    });
  }

  // --------------------------------------
  // FINAL ALWAYS-VALID SCHEMA
  // --------------------------------------
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: finalName,
    url: finalUrl,
    brand: {
      '@type': 'Brand',
      name: finalName
    },
    description: finalDescription,
    image: [finalImage],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: finalRatingValue,
      reviewCount: finalReviewCount,
      bestRating: 5,
      worstRating: 1
    },
    ...(reviewSchema.length > 0 ? { review: reviewSchema } : {})
  };
  useEffect(() => {
    if (isScript && !isFromList) {
      getReviewWidgetDetails();
    }
  }, []);

  useEffect(() => {
    if (!summaryRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setBoxWidth(entry.contentRect.width);
      }
    });

    observer.observe(summaryRef.current);

    return () => {
      observer.disconnect();
    };
  }, []); //  run once on mount

  useEffect(() => {
    if (!isFromList && (dynamicCustomCSS || previewCustomCss)) {
      injectCustomCSS(customCSS, id);
      setDynamicCustomCSS(null);
      dispatch(setPreviewCustomCss(null));
    }
  }, [dynamicCustomCSS, id, previewCustomCss]);

  useEffect(() => {
    const resize = () => {
      if (!summaryRef.current || !reviewTemplateRef.current) return;

      const containerWidth = summaryRef.current.getBoundingClientRect().width; // use summary box width
      const screenWidth =
        reviewTemplateRef.current.getBoundingClientRect().width;

      setReviewCardWidth(screenWidth || 'auto');

      const totalSlideWidth = (screenWidth < 600 ? 260 : 280) + CARD_GAP;
      let perSlide = Math.floor((containerWidth + CARD_GAP) / totalSlideWidth);
      if (screenWidth < 600 || isMobile) {
        perSlide = 1; // Always 1 on mobile
      } else {
        perSlide = Math.max(1, perSlide);
      }
      setSlidesToShow(perSlide);
    };

    // keep checking until refs are ready
    const interval = setInterval(() => {
      if (summaryRef.current && reviewTemplateRef.current) {
        resize();
        clearInterval(interval);
      }
    }, 100);

    window.addEventListener('resize', resize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resize);
    };
  }, []);


  const getReviewWidgetDetails = async () => {
    const response: any = await getReviewWidget(widgetId ? widgetId : id);
    if (response?.status == 'success') {
      injectCustomFont(
        response?.widgetDetails?.widget_details?.font || 'Inter'
      );
      setDynamicOptions(response?.widgetDetails?.widget_details);
      setDynamicCustomCSS(true);
      setRows(response?.reviewResults?.reviews);
      setLocationName(response?.locationName);
      setPlaceId(response?.placeId);
      setTotalReviewsRating(response?.reviewResults?.totalReviewsRating);
      setIsHidden(false);
    }
  };
  let dummyReviews = convertReviewRows(rows);

  const injectCustomCSS = (css: string, widgetId: string) => {
    if (!css || !css.trim()) return; //  stop if no CSS
    // Ensure uniqueness in case multiple widgets are rendered
    const styleId = `custom-style-${widgetId}`;
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    // Scope all CSS under the widget wrapper class to avoid conflicts
    const scopedCSS = css?.replace(
      /\.([a-zA-Z0-9-_]+)/g,
      `.widget-${widgetId} .$1`
    );

    styleTag.innerHTML = scopedCSS;
  };

  const cardsPerView = slidesToShow;

  const allFilteredReviews = dummyReviews?.filter((review, idx) => {
    // Rating filter (use destructured ratingFilter instead of options?.ratingFilter)
    const matchesRating =
      ratingFilter === 'All' || `${review?.stars} Stars` === ratingFilter;

    // Date filter
    const matchesDate =
      dateFilter === 'All'
        ? true
        : isWithinDateRange(review?.timestamp, dateFilter);

    // Include Rule
    const matchesInclude =
      !includeRule?.type || applyRule(includeRule, review.text);

    // Exclude Rule
    const matchesExclude =
      !excludeRule?.type || !applyRule(excludeRule, review.text);

    // Comment filter
    const matchesComment =
      !showOnlyCommentedReviews ||
      (review?.text && review.text.trim().length > 0);

    const finalResult =
      matchesRating &&
      matchesDate &&
      matchesInclude &&
      matchesExclude &&
      matchesComment;

    return finalResult;
  });

  // Only slice when isScript is false
  let filteredReviews =
    isScript && !isFromList
      ? allFilteredReviews
      : allFilteredReviews?.slice(0, 10);

  const totalPages = Math.ceil(filteredReviews?.length / cardsPerView);
  let currentPage = Math.floor(absoluteIndex / slidesToShow);
  // Total reviews
  const totalSlides = filteredReviews?.length || 0;

  if (absoluteIndex + slidesToShow >= totalSlides) {
    currentPage = totalPages - 1;
  }

  const renderSummaryBox = () => (
    <Box
      className="review-summary-box"
      sx={
        isMobile || isMobileUI
          ? S.summaryMobileBox(
              cardBgColor,
              textColor,
              starsColor,
              cardRadius,
              view
            )
          : S.summaryBox(cardBgColor, textColor, starsColor, cardRadius)
      }
      ref={summaryRef}
    >
      <Box
        className="review-summary-header"
        display="flex"
        flexDirection={'column'}
      >
        <Box display="flex" alignItems="center" gap={'8px'}>
          <img
            src={`${scriptURL}static/images/icons/local-seo/google.svg`}
            alt="icon"
            width={75}
            height={32}
          />
          <Typography sx={S.summarySpanText(textColor, font)}>
            {T.reviews}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={'8px'}>
          <Typography
            className="review-summary-rating"
            sx={S.summaryText(textColor)}
            fontFamily={font}
          >
            <span
              className="review-summary-rating-value"
              style={S.summarySpanText(textColor, font)}
            >
              {totalReviewsRating != null
                ? Number.isInteger(totalReviewsRating)
                  ? `${totalReviewsRating}.0`
                  : totalReviewsRating
                : '5.0'}
            </span>{' '}
          </Typography>

          <Box
            className="review-summary-stars"
            display="flex"
            alignItems="center"
            gap="4px"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <StarIcon
                key={i}
                className="review-summary-star-icon"
                sx={{ color: starsColor, fontSize: 20 }}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box
        className="review-summary-footer"
        display="flex"
        alignItems="center"
        gap="8px"
        sx={isMobile || isMobileUI ? S.summaryMobileReviewBox : {}}
      >
        <Typography
          className="review-summary-count"
          sx={S.summaryText(textColor)}
          fontFamily={font}
        >
          <span
            className="review-summary-count-value"
            style={
              isMobile || isMobileUI
                ? S.summarySpanMobileText(textColor, font)
                : S.summarySpanText(textColor, font)
            }
          >
            {dummyReviews?.length}
          </span>{' '}
          Reviews
        </Typography>
        {showCTAButton && (
          <Button
            className="review-summary-cta-button"
            variant="contained"
            sx={{
              ...S.rateUsBtn,
              width: isMobile || isMobileUI ? '100%' : 'auto',
              backgroundColor: accentColor,
              '&:hover': {
                backgroundColor: accentColor
              },
              color: buttonLabelColor,
              fontFamily: font
            }}
            onClick={() => {
              openNewTab(reviewUrl || '');
            }}
          >
            {buttonText || ''}
          </Button>
        )}
      </Box>
    </Box>
  );

  const renderReviewCard = (review: any, idx: number) => (
    <Box
      key={idx}
      className="review-card"
      sx={S.reviewBox(cardBgColor, textColor, isMobile, cardRadius)}
    >
      <Box
        className="review-card-top"
        display="flex"
        flexDirection="column"
        gap="8px"
      >
        <Box
          className="review-card-stars-row"
          display="flex"
          alignItems="center"
          gap="8px"
        >
          <Box className="review-card-stars" display="flex">
            {Array.from({ length: review.stars })?.map((_, i) => (
              <StarIcon
                key={i}
                className="review-card-star-icon"
                sx={{ color: starsColor, fontSize: 16 }}
                fontSize="small"
              />
            ))}
          </Box>
        </Box>

        <Typography
          className="review-card-text"
          sx={S.reviewText(textColor, font)}
          fontFamily={font}
        >
          {review.long && !expandedReviews[idx]
            ? review.text.slice(0, 150) + '...'
            : review.text}{' '}
          {review.long && (
            <span
              className="review-card-readmore"
              style={S.readMore(accentColor, font)}
              onClick={() => {
                setSelectedRow(review);
                setOpenViewModal(true);
                //toggleExpanded(idx);
              }}
            >
              {expandedReviews[idx] ? T.readLess : T.readMore}
            </span>
          )}
        </Typography>
      </Box>
      <Box
        className="review-card-bottom"
        display="flex"
        gap="12px"
        alignItems="center"
      >
        {showProfilePictures && (
          <Avatar
            className="review-card-avatar"
            src={review.image}
            alt={review.name}
            sx={S.avatar}
          />
        )}
        <Box className="review-card-reviewer" sx={S.reviewerDetails}>
          {showNames && (
            <Box display={'flex'} alignItems={'center'} gap={'4px'}>
              <Typography
                className="review-card-reviewer-name"
                fontFamily={font}
                sx={S.reviewerName(textColor, font)}
              >
                {review.name}
              </Typography>
              {!review?.isAnonymous && (
                <VerifiedIcon sx={{ color: accentColor, fontSize: 'var(--font-size-14)', }} />
              )}
            </Box>
          )}
          <Typography
            className="review-card-time"
            sx={S.reviewTime(textColor, font)}
          >
            {review.time}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const badgeUI = () => (
    <Box
      className="review-badge"
      sx={{
        ...S.badgeCard,
        backgroundColor: cardBgColor,
        borderRadius: cardRadius
      }}
    >
      <Box className="review-badge-logo">
        <img
          className="review-badge-google-icon"
          src={`${scriptURL}static/images/icons/local-seo/Google-logo.svg`}
          alt="icon"
          width={44}
          height={44}
        />
      </Box>
      <Box className="review-badge-text-group">
        <Box
          className="review-badge-stars-row"
          display="flex"
          alignItems="center"
          gap="4px"
        >
          <Box className="review-badge-stars" display="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <StarIcon
                key={i}
                className="review-badge-star-icon"
                sx={{ color: starsColor, fontSize: 20 }}
                fontSize="small"
              />
            ))}
          </Box>
          <Typography
            className="review-badge-score"
            sx={S.badgeReviewText(textColor, font)}
          >
            {totalReviewsRating != null
              ? Number.isInteger(totalReviewsRating)
                ? `${totalReviewsRating}.0`
                : totalReviewsRating
              : '5.0'}
          </Typography>
        </Box>
        <Typography
          className="review-badge-total"
          sx={S.badgeReviewText(textColor, font)}
        >
          {dummyReviews?.length}{' '}
          <span
            className="review-badge-total-label"
            style={{ fontWeight: 400 }}
          >
            Reviews
          </span>
        </Typography>
      </Box>
    </Box>
  );

  const goPrev = () => {
    const newIndex = Math.max(0, absoluteIndex - slidesToShow);
    setAbsoluteIndex(newIndex);
    sliderRef.current?.slickGoTo(newIndex - start);
  };
  const goNext = () => {
    const newIndex = Math.min(totalSlides - 1, absoluteIndex + slidesToShow);
    setAbsoluteIndex(newIndex);
    sliderRef.current?.slickGoTo(newIndex - start);
  };

  const PrevArrow = ({ sliderRef, currentPage }: any) => {
    if (currentPage <= 0) return null; // hide on very first page
    const handlePrev = () => {
      if (isAnimating) return; //  Ignore clicks during animation
      setIsAnimating(true);
      goPrev(); // <-- use global-aware navigation
      setTimeout(() => setIsAnimating(false), 600);
    };

    return (
      <Box
        onClick={handlePrev}
        sx={{
          ...S.absoluteArrow('left', accentColor),
          ...S.arrowBoxSx('left', bgColor)
        }}
      >
        <ArrowCircleLeftIcon
          sx={S?.arrowIconSx(accentColor, S.navIcon(accentColor).fontSize)}
        />
      </Box>
    );
  };

  const NextArrow = ({ sliderRef, currentPage, totalPages }: any) => {
    if (currentPage >= totalPages - 1) return null; // hide on very last page
    const handleNext = () => {
      if (isAnimating) return;
      setIsAnimating(true);
      //sliderRef.current?.slickNext(); //  smooth scroll forward
      goNext(); // <-- use global-aware navigation
      setTimeout(() => setIsAnimating(false), 600);
    };

    return (
      <Box
        onClick={handleNext}
        sx={{
          ...S.absoluteArrow('right', accentColor),
          ...S.arrowBoxSx('right', bgColor)
        }}
      >
        <ArrowCircleRightIcon
          sx={S?.arrowIconSx(accentColor, S.navIcon(accentColor).fontSize)}
        />
      </Box>
    );
  };

  const buffer = isMobile ? 200 : 500;

  let start = 0;
  let end = Math.min(totalSlides, buffer);

  // Only update start/end if absoluteIndex moves outside current buffer
  if (absoluteIndex < start) {
    start = absoluteIndex; // scroll up
    end = Math.min(totalSlides, start + buffer);
  } else if (absoluteIndex >= end) {
    start = absoluteIndex - buffer + slidesToShow; // scroll down
    start = Math.max(0, start); // clamp start
    end = Math.min(totalSlides, start + buffer);
  }

  const visibleReviews = filteredReviews?.slice(start, end);

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 600,
    arrows: true,
    slidesToShow: isMobile ? 1 : slidesToShow,
    slidesToScroll: isMobile ? 1 : slidesToShow, // scroll full pages
    variableWidth: false, //  fix alignment
    // lazyLoad: 'ondemand' as LazyLoadTypes, //  fixed typing
    cssEase: 'ease-in-out', //  feels natural
    waitForAnimate: true, // ensures no overlap animation
    initialSlide: 0,
    adaptiveHeight: false, // important to avoid height recalculation
    beforeChange: () => setIsAnimating(true),
    afterChange: (localIndex: number) => {
      const newAbsoluteIndex = start + localIndex; // map back to global
      setAbsoluteIndex(newAbsoluteIndex);
      setIsAnimating(false);
    },
    prevArrow: (
      <PrevArrow
        currentPage={currentPage}
        totalPages={totalPages}
        sliderRef={sliderRef}
      />
    ),
    nextArrow: (
      <NextArrow
        currentPage={currentPage}
        totalPages={totalPages}
        sliderRef={sliderRef}
      />
    ),
    appendDots: () => {
      // Total number of reviews available (use filteredReviews if provided, fallback to global totalSlides)
      const totalReviews = filteredReviews?.length ?? totalSlides;

      // Maximum number of dots that can be displayed at once
      const maxVisibleDots = 5;

      // If total reviews are fewer than the max limit, only show that many dots
      const effectiveMaxDots =
        totalReviews < maxVisibleDots
          ? totalReviews
          : totalPages < maxVisibleDots
          ? totalPages
          : maxVisibleDots;

      let visibleDots: number[] = [];

      if (totalReviews <= effectiveMaxDots) {
        // Case 1: If total reviews are less than or equal to effectiveMaxDots
        // → Show one dot per review
        visibleDots = [...Array(totalReviews).keys()];
      } else {
        // Case 2: More reviews than maxVisibleDots → Show a "sliding window" of dots
        let startIndex = currentPage - Math.floor(effectiveMaxDots / 2);
        let endIndex = startIndex + effectiveMaxDots;

        // Edge case: if window goes before first review
        if (startIndex < 0) {
          startIndex = 0;
          endIndex = effectiveMaxDots;
        }
        // Edge case: if window goes past last review
        else if (endIndex > totalReviews) {
          endIndex = totalReviews;
          startIndex = totalReviews - effectiveMaxDots;
        }

        visibleDots = [...Array(endIndex - startIndex).keys()]?.map(
          (dotIndex) => dotIndex + startIndex
        );
      }

      // Render the dots container
      return (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            gap: '6px',
            pointerEvents: 'auto',
            zIndex: 10
          }}
        >
          {visibleDots?.map((globalDotIndex) => {
            const isActive = globalDotIndex === currentPage;
            const distanceFromActive = Math.abs(globalDotIndex - currentPage);

            // Active dot = larger & fully opaque
            // Non-active dots shrink & fade depending on distance from active
            const scale = isActive
              ? 1.2
              : Math.max(0.6, 1 - distanceFromActive * 0.2);
            const opacity = isActive
              ? 1
              : Math.max(0.5, 1 - distanceFromActive * 0.2);

            return (
              <Box
                key={globalDotIndex}
                onClick={() => {
                  if (isAnimating) return;

                  // Calculate absolute index to navigate correctly
                  const goToIndex = globalDotIndex * slidesToShow;

                  // Update global index state
                  setAbsoluteIndex(goToIndex);

                  // Move slick carousel to the relative index in the local slice
                  sliderRef.current?.slickGoTo(goToIndex - start);
                }}
                sx={{
                  pointerEvents: isAnimating ? 'none' : 'auto',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: accentColor,
                  transform: `scale(${scale})`,
                  opacity,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              />
            );
          })}
        </Box>
      );
    },

    customPaging: () => <></> // disable default dots
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
  };

  const getStarRatingValue = (starRating) => {
    const ratingMap = {
      ONE: 1,
      TWO: 2,
      THREE: 3,
      FOUR: 4,
      FIVE: 5
    };

    return ratingMap[starRating?.toUpperCase()] ?? null;
  };

  const injectCustomFont = (fontText) => {
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = `https://fonts.googleapis.com/css2?family=${fontText.replace(
      / /g,
      '+'
    )}:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap`;
    document.head.appendChild(fontLink);
  };
  return (
    <>
      {isScript && !isHidden && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      )}
      {!isScript ? (
        <Card sx={{ padding: '0px' }}>
          <CardContent sx={{ ...S.cardContent, ...S.wrapper }}>
            {/* Header */}
            <Box sx={{ ...S.spacing, ...S.headerRow }}>
              <Box sx={S.titleBox}>
                <Typography sx={S.title}>{T.title}</Typography>
                <CustomTooltip
                  title={
                    <Box>
                      <Typography
                        sx={keywordResearchDashBoardCss?.customTollTipText}
                      >
                        {T.tooltipText}
                      </Typography>
                    </Box>
                  }
                  placement="bottom"
                  arrow
                >
                  <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
                </CustomTooltip>
              </Box>
              <Box display="flex" gap="8px">
                <Button
                  onClick={() => {
                    setView('desktop');
                  }}
                  sx={S.toggleButton(view === 'desktop')}
                >
                  <img
                    src="/static/images/icons/desktop.svg" // Replace with your actual image path
                    alt="icon"
                    style={{
                      marginRight: '4px',
                      ...KeywordTrackingAnalyzeCss?.imgStyle
                    }} // Adjust size as needed
                  />
                  <Typography sx={widgetPreviewSx?.toggleBtnText}>
                    {T.desktop}
                  </Typography>
                </Button>
                <Button
                  onClick={() => {
                    setView('mobile');
                  }}
                  sx={S.toggleButton(view === 'mobile')}
                >
                  <img
                    src="/static/images/icons/mobile.svg"
                    alt="icon"
                    style={{
                      marginRight: '4px',
                      ...KeywordTrackingAnalyzeCss?.imgStyle
                    }} // Adjust size as needed
                  />
                  <Typography sx={widgetPreviewSx?.toggleBtnText}>
                    {T.mobile}
                  </Typography>
                </Button>
              </Box>
            </Box>

            <Box
              className={`review-template-wrapper widget-${id}`}
              sx={{
                ...S.spacingExtraPy,
                backgroundColor: template === 'badge' ? 'transparent' : bgColor,
                borderRadius: cardRadius
              }}
              ref={reviewTemplateRef}
            >
              {/* Badge View */}
              {template === 'badge' && badgeUI()}

              {/* Summary Box */}
              {template === 'carousel' && renderSummaryBox()}

              {/* Cards */}
              {template === 'carousel' && filteredReviews?.length > 0 && (
                <Box
                  className="review-carousel-container"
                  mt={4}
                  sx={S.carouselTemplateMainBox}
                >
                  <Box
                    sx={{
                      width: isMobile
                        ? {
                            xs: view === 'mobile' ? '300px' : 'auto',
                            sm: 'auto'
                          }
                        : boxWidth || '100%',
                      minWidth: isMobile ? 'auto' : boxWidth || '100%',
                      maxWidth: isMobile
                        ? {
                            xs: view === 'mobile' ? '300px' : 'auto',
                            sm: 'auto'
                          }
                        : boxWidth || '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      '& .slick-dots': {
                        bottom: '-35px !important'
                      },
                      position: 'relative',
                      paddingBottom: '40px', // reserve space for dots
                      overflow: 'visible'
                    }}
                    ref={containerRef}
                  >
                    <Slider ref={sliderRef} {...sliderSettings}>
                      {visibleReviews?.map((review, idx) => {
                        const realIndex = start + idx; // index in the filteredReviews array

                        return (
                          <Box
                            key={realIndex}
                            sx={{
                              px: 1,
                              width: {
                                xs:
                                  view === 'mobile'
                                    ? '300px !important'
                                    : '100% !important',
                                sm: '100% !important'
                              },

                              flexShrink: 0
                            }}
                          >
                            {renderReviewCard(review, realIndex)}
                          </Box>
                        );
                      })}
                    </Slider>
                  </Box>
                </Box>
              )}

              {template === 'carousel' && filteredReviews?.length === 0 && (
                <Typography
                  className="review-no-data-text"
                  sx={S.noReviews(textColor)}
                >
                  {T.noReviews}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : isHidden && !isFromList ? (
        <></>
      ) : (
        <>
          <Box
            className={`review-template-wrapper widget-${id}`}
            sx={{
              ...S.spacingExtraPy,
              backgroundColor: template === 'badge' ? 'transparent' : bgColor,
              borderRadius: cardRadius
            }}
            ref={reviewTemplateRef}
          >
            {/* Badge View */}
            {template === 'badge' && badgeUI()}

            {/* Summary Box */}
            {template === 'carousel' && renderSummaryBox()}

            {/* Cards */}
            {template === 'carousel' && filteredReviews?.length > 0 && (
              <Box
                className="review-carousel-container"
                mt={4}
                sx={S.carouselTemplateMainBox}
              >
                <Box
                  sx={{
                    width: isMobile
                      ? { xs: view === 'mobile' ? '300px' : 'auto', sm: 'auto' }
                      : boxWidth || '100%',
                    minWidth: isMobile ? 'auto' : boxWidth || '100%',
                    maxWidth: isMobile
                      ? { xs: view === 'mobile' ? '300px' : 'auto', sm: 'auto' }
                      : boxWidth || '100%',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    paddingBottom: '40px', // reserve space for dots
                    overflow: 'visible', // ensure dots don't push height
                    '& .slick-dots': {
                      bottom: '-35px !important'
                    },
                    position: 'relative'
                  }}
                  ref={containerRef}
                >
                  <Slider ref={sliderRef} {...sliderSettings}>
                    {visibleReviews?.map((review, idx) => {
                      const realIndex = start + idx; // index in the filteredReviews array

                      return (
                        <Box
                          key={realIndex}
                          sx={{
                            paddingLeft: '8px',
                            paddingRight: '8px',

                            width: 'fit-content !important',
                            '@supports (width: -webkit-fill-available)': {
                              width: '-webkit-fill-available !important'
                            },
                            flexShrink: 0
                          }}
                        >
                          {renderReviewCard(review, realIndex)}
                        </Box>
                      );
                    })}
                  </Slider>
                </Box>
              </Box>
            )}

            {template === 'carousel' && filteredReviews?.length === 0 && (
              <Typography
                className="review-no-data-text"
                sx={S.noReviews(textColor)}
              >
                {T.noReviews}
              </Typography>
            )}
          </Box>
        </>
      )}
      <CommonModal
        open={openViewModal}
        handCloseModal={handleCloseViewModal}
        title={''}
        closebtn={false}
        isAgencyModal={true}
        isInfoModal={true}
        isFreeDowngrade={true}
        isDynamicWidth={'610px'}
        lowBlur={true}
        blur={null}
      >
        <ViewReviewModal
          handleCloseViewModal={handleCloseViewModal}
          row={selectedRow}
          getStarRatingValue={getStarRatingValue}
          isWidget={true}
          options={{
            accentColor,
            font,
            bgColor,
            cardBgColor,
            textColor,
            starsColor
          }}
          locationName={locationName}
        />
      </CommonModal>
    </>
  );
};
